import React, { useMemo, useState } from "react";
import {
  AbsoluteFill,
  Audio,
  Sequence,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  Easing,
  spring,
} from "remotion";
import { linearTiming, TransitionSeries } from "@remotion/transitions";
import { slide } from "@remotion/transitions/slide";
import { fade } from "@remotion/transitions/fade";
import { z } from "zod";
import CroppedImg from "../components/CroppedImg";

// ─── Zod Schema ─────────────────────────────────────────────────────────────

const FeatureBadgeSchema = z.object({
  icon: z.string().default("✨"),
  label: z.string().default("Feature"),
});

const ColorSwatchSchema = z.object({
  name: z.string().default("Natural Black"),
  color: z.string().default("#1a1a1a"),
});

export const WigGlowUpPropsSchema = z.object({
  // Product info
  brandName: z
    .string()
    .default("LUXE HAIR CO.")
    .describe("Brand name displayed in intro"),
  productName: z
    .string()
    .default("Brazilian Body Wave")
    .describe("Wig product name"),
  tagline: z
    .string()
    .default("Your Next Signature Look")
    .describe("Catchy tagline"),
  introTitle: z
    .string()
    .default("MY\nREVIEW")
    .describe("Main intro splash text (use \\n for line breaks)"),
  // Images
  heroImage: z
    .string()
    .default("")
    .describe("Main hero wig image URL"),
  carouselImages: z
    .array(z.string())
    .default([])
    .describe("Array of wig images for the carousel"),
  galleryImages: z
    .array(z.string())
    .default([])
    .describe("Array of images for the gallery burst section"),

  // Features
  features: z
    .array(FeatureBadgeSchema)
    .default([
      { icon: "💇‍♀️", label: "100% Human Hair" },
      { icon: "📏", label: '16"-28" Length' },
      { icon: "🔥", label: "Heat Friendly" },
      { icon: "💧", label: "Wash & Restyle" },
      { icon: "✨", label: "Pre-Plucked" },
      { icon: "🎀", label: "HD Lace Front" },
    ])
    .describe("Feature badges to display"),

  // Color swatches
  colorSwatches: z
    .array(ColorSwatchSchema)
    .default([
      { name: "Natural Black", color: "#1a1a1a" },
      { name: "Dark Brown", color: "#3d2314" },
      { name: "Honey Blonde", color: "#c68e3f" },
      { name: "Burgundy", color: "#722f37" },
      { name: "Ombré", color: "linear-gradient(135deg, #1a1a1a, #c68e3f)" },
    ])
    .describe("Available color swatches"),

  // Toggles
  showIntro: z
    .boolean()
    .default(false)
    .describe("Show the intro splash screen"),
  showOutro: z
    .boolean()
    .default(false)
    .describe("Show the CTA outro screen"),

  // CTA
  ctaText: z
    .string()
    .default("SHOP NOW")
    .describe("Call to action button text"),
  ctaSubtext: z
    .string()
    .default("Link in Bio 🔗")
    .describe("Secondary CTA text"),
  handle: z
    .string()
    .default("@luxehairco")
    .describe("Social media handle"),

  // Timing (in frames at 30fps)
  introDuration: z.number().default(75).describe("Intro splash duration"),
  heroDuration: z.number().default(120).describe("Hero reveal duration"),
  carouselDuration: z.number().default(180).describe("Carousel duration"),
  featureDuration: z.number().default(120).describe("Feature highlights duration"),
  colorDuration: z.number().default(90).describe("Color swatch section duration"),
  galleryDuration: z.number().default(150).describe("Gallery burst duration"),
  outroDuration: z.number().default(90).describe("CTA outro duration"),
  transitionDuration: z.number().default(20).describe("Transition duration between sections"),

  // Style
  accentColor: z.string().default("#FF6B9D").describe("Primary accent color (pink/magenta)"),
  accentColor2: z.string().default("#C084FC").describe("Secondary accent (purple)"),
  accentColor3: z.string().default("#60A5FA").describe("Tertiary accent (blue)"),
  darkBg: z.string().default("#0A0A0A").describe("Dark background color"),

  // Audio
  audioSrc: z.string().optional().describe("Optional audio file path"),
  audioVolume: z.number().min(0).max(1).default(0.8).describe("Audio volume"),
  audioStartFrom: z.number().default(0).describe("Audio start time in seconds"),
  audioDurationOverride: z
    .number()
    .default(30)
    .describe("Audio duration in seconds"),
});

// ─── Helper: get image source ─────────────────────────────────────────────────
const getImgSrc = (src) => {
  if (!src) return "";
  if (src.startsWith("http://") || src.startsWith("https://")) return src;
  return staticFile(src);
};

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * IntroSplash – Neon gradient text reveal with shimmer particles
 */
const IntroSplash = ({ brandName, tagline, introTitle, accentColor, accentColor2, accentColor3, darkBg }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Brand name spring
  const brandSpring = spring({ fps, frame, config: { damping: 12, stiffness: 80, mass: 0.8 } });
  // Tagline delayed spring
  const tagSpring = spring({ fps, frame: frame - 15, config: { damping: 14, stiffness: 60, mass: 1 } });

  // Shimmer line sweep
  const shimmerX = interpolate(frame, [20, 55], [-100, 120], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Background gradient rotation
  const gradAngle = interpolate(frame, [0, 75], [135, 200], { extrapolateRight: "clamp" });

  // Particle positions (decorative floating dots)
  const particles = useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => ({
        x: (i * 137.508) % 100,
        y: (i * 61.803) % 100,
        size: 3 + (i % 4) * 2,
        speed: 0.3 + (i % 3) * 0.2,
        delay: i * 3,
      })),
    []
  );

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(${gradAngle}deg, ${darkBg} 0%, #1a0a2e 40%, ${darkBg} 100%)`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {/* Floating particles */}
      {particles.map((p, i) => {
        const pY = interpolate(frame - p.delay, [0, 100], [p.y + 10, p.y - 20], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });
        const pOpacity = interpolate(frame - p.delay, [0, 10, 60, 75], [0, 0.6, 0.6, 0], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${p.x}%`,
              top: `${pY}%`,
              width: p.size,
              height: p.size,
              borderRadius: "50%",
              background: i % 3 === 0 ? accentColor : i % 3 === 1 ? accentColor2 : accentColor3,
              opacity: pOpacity,
              filter: "blur(1px)",
            }}
          />
        );
      })}

      {/* Decorative gradient orbs */}
      <div
        style={{
          position: "absolute",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${accentColor}33 0%, transparent 70%)`,
          top: "15%",
          left: "10%",
          filter: "blur(60px)",
          opacity: interpolate(frame, [0, 30], [0, 0.8], { extrapolateRight: "clamp" }),
        }}
      />
      <div
        style={{
          position: "absolute",
          width: 350,
          height: 350,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${accentColor2}33 0%, transparent 70%)`,
          bottom: "20%",
          right: "5%",
          filter: "blur(50px)",
          opacity: interpolate(frame, [10, 40], [0, 0.7], { extrapolateRight: "clamp" }),
        }}
      />

      {/* Brand name */}
      <div
        style={{
          fontSize: 42,
          fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
          fontWeight: 300,
          letterSpacing: 14,
          color: "#ffffff88",
          textTransform: "uppercase",
          transform: `translateY(${interpolate(brandSpring, [0, 1], [60, 0])}px)`,
          opacity: brandSpring,
          marginBottom: 20,
        }}
      >
        {brandName}
      </div>

      {/* MY REVIEW main text with gradient */}
      <div
        style={{
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            fontSize: 130,
            fontFamily: "'Impact', 'Anton', 'Arial Black', system-ui, sans-serif",
            fontWeight: 900,
            background: `linear-gradient(135deg, ${accentColor}, ${accentColor2}, ${accentColor3})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            letterSpacing: 4,
            transform: `scale(${interpolate(brandSpring, [0, 1], [0.6, 1])})`,
            opacity: brandSpring,
            textAlign: "center",
            lineHeight: 1.1,
          }}
        >
          {(introTitle || 'MY\nREVIEW').split(/\\n|\n/).map((line, i, arr) => (
            <React.Fragment key={i}>
              {line}
              {i < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </div>

        {/* Shimmer sweep */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: `${shimmerX}%`,
            width: "30%",
            height: "100%",
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
            transform: "skewX(-15deg)",
            pointerEvents: "none",
          }}
        />
      </div>

      {/* Tagline */}
      <div
        style={{
          fontSize: 38,
          fontFamily: "'Inter', system-ui, sans-serif",
          fontWeight: 400,
          color: "#ffffffcc",
          marginTop: 30,
          transform: `translateY(${interpolate(tagSpring, [0, 1], [40, 0])}px)`,
          opacity: Math.max(0, tagSpring),
          textAlign: "center",
          fontStyle: "italic",
        }}
      >
        {tagline}
      </div>

      {/* Accent line */}
      <div
        style={{
          width: interpolate(frame, [25, 50], [0, 200], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          }),
          height: 3,
          background: `linear-gradient(90deg, ${accentColor}, ${accentColor2})`,
          marginTop: 24,
          borderRadius: 2,
        }}
      />
    </AbsoluteFill>
  );
};

/**
 * HeroReveal – Cinematic hero image with diagonal wipe, floating tagline badge
 */
const HeroReveal = ({
  heroImage,
  heroCrop,
  productName,
  tagline,
  accentColor,
  accentColor2,
  darkBg,
}) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // Diagonal wipe reveal
  const wipeProgress = interpolate(frame, [0, 35], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.bezier(0.25, 0.1, 0.25, 1),
  });

  // Ken Burns slow zoom
  const zoom = interpolate(frame, [0, 120], [1.05, 1.15], { extrapolateRight: "clamp" });
  const panX = interpolate(frame, [0, 120], [0, -2], { extrapolateRight: "clamp" });

  // Tagline badge spring
  const badgeSpring = spring({
    fps,
    frame: frame - 30,
    config: { damping: 10, stiffness: 100, mass: 0.6 },
  });

  // Product name slide
  const nameSpring = spring({
    fps,
    frame: frame - 20,
    config: { damping: 14, stiffness: 70, mass: 0.8 },
  });

  // Vignette overlay intensity
  const vignetteOpacity = interpolate(frame, [0, 20], [0, 0.5], { extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ background: darkBg, overflow: "hidden" }}>
      {/* Hero image with clip reveal */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          clipPath: `polygon(0 0, ${wipeProgress * 100 + 15}% 0, ${wipeProgress * 100}% 100%, 0% 100%)`,
        }}
      >
        <CroppedImg
          src={getImgSrc(heroImage)}
          crop={heroCrop}
          style={{
            transform: `scale(${zoom}) translateX(${panX}%)`,
          }}
        />
      </div>

      {/* Second pass of the wipe for full reveal */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          clipPath: `polygon(${Math.max(0, wipeProgress * 100 - 10)}% 0, 100% 0, 100% 100%, ${Math.max(0, wipeProgress * 100 - 25)}% 100%)`,
          opacity: interpolate(frame, [10, 35], [0, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          }),
        }}
      >
        <CroppedImg
          src={getImgSrc(heroImage)}
          crop={heroCrop}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: `scale(${zoom}) translateX(${panX}%)`,
          }}
        />
      </div>

      {/* Gradient vignette overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(180deg, transparent 30%, ${darkBg}ee 85%, ${darkBg} 100%)`,
          opacity: vignetteOpacity,
        }}
      />

      {/* Accent glow top-right */}
      <div
        style={{
          position: "absolute",
          top: -100,
          right: -100,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${accentColor}22 0%, transparent 70%)`,
          filter: "blur(40px)",
          opacity: interpolate(frame, [20, 50], [0, 1], { extrapolateRight: "clamp" }),
        }}
      />

      {/* Product name at bottom */}
      <div
        style={{
          position: "absolute",
          bottom: 260,
          left: 60,
          right: 60,
          transform: `translateY(${interpolate(nameSpring, [0, 1], [50, 0])}px)`,
          opacity: Math.max(0, nameSpring),
        }}
      >
        <div
          style={{
            fontSize: 64,
            fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
            fontWeight: 800,
            color: "#ffffff",
            lineHeight: 1.15,
            textShadow: "0 4px 30px rgba(0,0,0,0.6)",
          }}
        >
          {productName}
        </div>
      </div>

      {/* Floating tagline badge */}
      <div
        style={{
          position: "absolute",
          bottom: 140,
          left: 60,
          display: "flex",
          alignItems: "center",
          gap: 16,
          transform: `translateY(${interpolate(badgeSpring, [0, 1], [40, 0])}px) scale(${interpolate(badgeSpring, [0, 1], [0.8, 1])})`,
          opacity: Math.max(0, badgeSpring),
        }}
      >
        {/* Tagline pill */}
        <div
          style={{
            background: `linear-gradient(135deg, ${accentColor}, ${accentColor2})`,
            padding: "14px 32px",
            borderRadius: 50,
            display: "flex",
            alignItems: "center",
            gap: 14,
            boxShadow: `0 8px 32px ${accentColor}66`,
          }}
        >
          <span
            style={{
              fontSize: 34,
              fontWeight: 700,
              color: "#fff",
              fontFamily: "'Inter', system-ui, sans-serif",
              fontStyle: "italic",
            }}
          >
            {tagline}
          </span>
        </div>
      </div>
    </AbsoluteFill>
  );
};

/**
 * ImageCarousel – Horizontal card carousel
 */
const ImageCarousel = ({ images, crops, accentColor, accentColor2, darkBg }) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  const cardWidth = width * 0.78;
  const cardHeight = height * 0.65;
  const cardGap = 30;
  const framesPerCard = 40;

  // Which card is currently "centered"
  const activeFloat = frame / framesPerCard;
  const activeIndex = Math.min(Math.floor(activeFloat), images.length - 1);

  // Smooth scroll offset
  const scrollX = interpolate(frame, [0, (images.length - 0.3) * framesPerCard], [0, -(images.length - 1) * (cardWidth + cardGap)], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.bezier(0.25, 0.1, 0.25, 1),
  });

  // Indicator dots
  const dotCount = images.length;

  return (
    <AbsoluteFill
      style={{
        background: darkBg,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Section header */}
      <div
        style={{
          paddingTop: 80,
          paddingLeft: 60,
          paddingRight: 60,
          marginBottom: 40,
        }}
      >
        <div
          style={{
            fontSize: 22,
            fontFamily: "'Inter', system-ui, sans-serif",
            fontWeight: 500,
            letterSpacing: 6,
            color: accentColor,
            textTransform: "uppercase",
            opacity: interpolate(frame, [0, 15], [0, 1], { extrapolateRight: "clamp" }),
          }}
        >
          STYLE GALLERY
        </div>
        <div
          style={{
            fontSize: 52,
            fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
            fontWeight: 800,
            color: "#fff",
            marginTop: 8,
            opacity: interpolate(frame, [5, 20], [0, 1], { extrapolateRight: "clamp" }),
          }}
        >
          Style Gallery
        </div>
      </div>

      {/* Cards container */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          flex: 1,
          paddingLeft: (width - cardWidth) / 2,
          transform: `translateX(${scrollX}px)`,
        }}
      >
        {images.map((img, i) => {
          const distFromActive = Math.abs(i - activeFloat);
          const cardScale = interpolate(distFromActive, [0, 1, 2], [1, 0.88, 0.78], {
            extrapolateRight: "clamp",
          });
          const cardOpacity = interpolate(distFromActive, [0, 1.5], [1, 0.4], {
            extrapolateRight: "clamp",
          });
          const cardRotate = interpolate(i - activeFloat, [-2, 0, 2], [4, 0, -4], {
            extrapolateRight: "clamp",
            extrapolateLeft: "clamp",
          });

          return (
            <div
              key={i}
              style={{
                flexShrink: 0,
                width: cardWidth,
                height: cardHeight,
                marginRight: cardGap,
                borderRadius: 24,
                overflow: "hidden",
                transform: `scale(${cardScale}) rotate(${cardRotate}deg)`,
                opacity: cardOpacity,
                boxShadow: `0 20px 60px rgba(0,0,0,0.5)`,
                border: `2px solid ${i === activeIndex ? accentColor + "66" : "transparent"}`,
                transition: "border 0.1s",
              }}
            >
              <CroppedImg
                src={getImgSrc(img)}
                crop={crops?.[i]}
              />
            </div>
          );
        })}
      </div>

      {/* Dot indicators */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 10,
          paddingBottom: 70,
          paddingTop: 30,
        }}
      >
        {Array.from({ length: dotCount }, (_, i) => {
          const isActive = i === activeIndex;
          return (
            <div
              key={i}
              style={{
                width: isActive ? 28 : 10,
                height: 10,
                borderRadius: 5,
                background: isActive
                  ? `linear-gradient(90deg, ${accentColor}, ${accentColor2})`
                  : "#ffffff33",
                transition: "all 0.2s",
              }}
            />
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

/**
 * FeatureHighlights – Animated feature badges popping in with stagger
 */
const FeatureHighlights = ({ features, accentColor, accentColor2, darkBg, productName }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  return (
    <AbsoluteFill
      style={{
        background: darkBg,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 60,
        overflow: "hidden",
      }}
    >
      {/* Background accent glow */}
      <div
        style={{
          position: "absolute",
          width: 600,
          height: 600,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${accentColor}15 0%, transparent 70%)`,
          filter: "blur(80px)",
          top: "20%",
          left: "50%",
          transform: "translateX(-50%)",
        }}
      />

      {/* Section title */}
      <div
        style={{
          fontSize: 28,
          fontFamily: "'Inter', system-ui, sans-serif",
          fontWeight: 500,
          letterSpacing: 8,
          color: accentColor,
          textTransform: "uppercase",
          marginBottom: 16,
          opacity: interpolate(frame, [0, 15], [0, 1], { extrapolateRight: "clamp" }),
        }}
      >
        WHY YOU'LL LOVE IT
      </div>
      <div
        style={{
          fontSize: 48,
          fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
          fontWeight: 800,
          color: "#fff",
          marginBottom: 60,
          textAlign: "center",
          opacity: interpolate(frame, [5, 20], [0, 1], { extrapolateRight: "clamp" }),
        }}
      >
        {productName}
      </div>

      {/* Feature badges grid */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 20,
          maxWidth: 900,
        }}
      >
        {features.map((feat, i) => {
          const stagger = i * 8;
          const badgeSpring = spring({
            fps,
            frame: frame - 15 - stagger,
            config: { damping: 10, stiffness: 120, mass: 0.5 },
          });

          return (
            <div
              key={i}
              style={{
                background: "rgba(255,255,255,0.06)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 20,
                padding: "22px 32px",
                display: "flex",
                alignItems: "center",
                gap: 14,
                transform: `translateY(${interpolate(badgeSpring, [0, 1], [30, 0])}px) scale(${interpolate(badgeSpring, [0, 1], [0.7, 1])})`,
                opacity: Math.max(0, badgeSpring),
              }}
            >
              <span style={{ fontSize: 34 }}>{feat.icon}</span>
              <span
                style={{
                  fontSize: 26,
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontWeight: 600,
                  color: "#ffffffdd",
                }}
              >
                {feat.label}
              </span>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

/**
 * ColorSwatchSection – Animated color option circles
 */
const ColorSwatchSection = ({ colorSwatches, accentColor, accentColor2, darkBg, productName }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Active swatch cycles
  const framesPerSwatch = 15;
  const activeIndex = Math.floor(frame / framesPerSwatch) % colorSwatches.length;

  return (
    <AbsoluteFill
      style={{
        background: darkBg,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {/* "Available in" text */}
      <div
        style={{
          fontSize: 26,
          fontFamily: "'Inter', system-ui, sans-serif",
          fontWeight: 400,
          letterSpacing: 6,
          color: "#ffffff88",
          textTransform: "uppercase",
          marginBottom: 20,
          opacity: interpolate(frame, [0, 12], [0, 1], { extrapolateRight: "clamp" }),
        }}
      >
        AVAILABLE IN
      </div>

      <div
        style={{
          fontSize: 56,
          fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
          fontWeight: 800,
          color: "#ffffff",
          marginBottom: 60,
          textAlign: "center",
          opacity: interpolate(frame, [5, 18], [0, 1], { extrapolateRight: "clamp" }),
        }}
      >
        {colorSwatches.length} Stunning Shades
      </div>

      {/* Swatch circles */}
      <div
        style={{
          display: "flex",
          gap: 28,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {colorSwatches.map((swatch, i) => {
          const stagger = i * 6;
          const swatchSpring = spring({
            fps,
            frame: frame - 10 - stagger,
            config: { damping: 12, stiffness: 100, mass: 0.5 },
          });
          const isActive = i === activeIndex;
          const pulseScale = isActive
            ? 1 + 0.08 * Math.sin((frame * Math.PI) / 10)
            : 1;

          return (
            <div
              key={i}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 12,
                transform: `translateY(${interpolate(swatchSpring, [0, 1], [40, 0])}px)`,
                opacity: Math.max(0, swatchSpring),
              }}
            >
              <div
                style={{
                  width: isActive ? 100 : 80,
                  height: isActive ? 100 : 80,
                  borderRadius: "50%",
                  background: swatch.color,
                  border: isActive
                    ? `4px solid ${accentColor}`
                    : "3px solid rgba(255,255,255,0.2)",
                  transform: `scale(${pulseScale})`,
                  boxShadow: isActive
                    ? `0 0 30px ${accentColor}44, 0 8px 24px rgba(0,0,0,0.4)`
                    : "0 4px 12px rgba(0,0,0,0.3)",
                }}
              />
              <span
                style={{
                  fontSize: 18,
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontWeight: isActive ? 700 : 400,
                  color: isActive ? "#fff" : "#ffffff88",
                  textAlign: "center",
                  maxWidth: 90,
                  lineHeight: 1.2,
                }}
              >
                {swatch.name}
              </span>
            </div>
          );
        })}
      </div>

      {/* Active color name highlight */}
      <div
        style={{
          marginTop: 50,
          fontSize: 32,
          fontFamily: "'Inter', system-ui, sans-serif",
          fontWeight: 600,
          background: `linear-gradient(90deg, ${accentColor}, ${accentColor2})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        }}
      >
        {colorSwatches[activeIndex]?.name}
      </div>
    </AbsoluteFill>
  );
};

/**
 * GalleryBurst – Polaroid-scattered images that assemble into a clean layout
 */
const GalleryBurst = ({ images, crops, accentColor, darkBg }) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  const assembleStart = 40;

  // Predefined scattered positions (percentage-based)
  const scatteredPositions = useMemo(
    () =>
      images.map((_, i) => ({
        x: 10 + ((i * 31) % 60),
        y: 10 + ((i * 47) % 65),
        rotation: -20 + ((i * 17) % 40),
        scale: 0.55 + (i % 3) * 0.15,
      })),
    [images.length]
  );

  // Grid positions – adaptive layout based on image count
  const count = Math.min(images.length, 6);
  const headerSpace = 200; // space reserved for header
  const padding = 40;
  const gap = 16;
  const availableW = width - padding * 2;
  const availableH = height - headerSpace - padding;

  const layout = useMemo(() => {
    if (count === 1) {
      const cW = availableW;
      const cH = availableH;
      return { cols: 1, cellW: cW, cellH: cH };
    }
    if (count === 2) {
      const cW = availableW;
      const cH = (availableH - gap) / 2;
      return { cols: 1, cellW: cW, cellH: cH };
    }
    if (count === 3) {
      // Top row: 1 large, bottom row: 2 side-by-side
      const cW = (availableW - gap) / 2;
      const cH = (availableH - gap) / 2;
      return { cols: 2, cellW: cW, cellH: cH, custom: true };
    }
    if (count === 4) {
      const cW = (availableW - gap) / 2;
      const cH = (availableH - gap) / 2;
      return { cols: 2, cellW: cW, cellH: cH };
    }
    // 5-6 images: 2 columns, smaller cells
    const cols = 2;
    const cW = (availableW - gap) / cols;
    const rows = Math.ceil(count / cols);
    const cH = (availableH - gap * (rows - 1)) / rows;
    return { cols, cellW: cW, cellH: cH };
  }, [count, availableW, availableH]);

  const { cols, cellW, cellH } = layout;

  const gridPositions = useMemo(() => {
    if (count === 3 && layout.custom) {
      // First image spans full width on top
      return [
        { x: padding, y: headerSpace },
        { x: padding, y: headerSpace + cellH + gap },
        { x: padding + cellW + gap, y: headerSpace + cellH + gap },
      ];
    }
    return images.slice(0, 6).map((_, i) => ({
      x: padding + (i % cols) * (cellW + gap),
      y: headerSpace + Math.floor(i / cols) * (cellH + gap),
    }));
  }, [count, cols, cellW, cellH, layout.custom]);

  return (
    <AbsoluteFill style={{ background: darkBg, overflow: "hidden" }}>
      {/* Header */}
      <div
        style={{
          position: "absolute",
          top: 70,
          left: 60,
          right: 60,
          opacity: interpolate(frame, [0, 15], [0, 1], { extrapolateRight: "clamp" }),
        }}
      >
        <div
          style={{
            fontSize: 22,
            fontFamily: "'Inter', system-ui, sans-serif",
            letterSpacing: 6,
            color: accentColor,
            fontWeight: 500,
            textTransform: "uppercase",
          }}
        >
          THE COLLECTION
        </div>
        <div
          style={{
            fontSize: 48,
            fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
            fontWeight: 800,
            color: "#fff",
            marginTop: 8,
          }}
        >
          Every Angle
        </div>
      </div>

      {/* Images */}
      {images.slice(0, 6).map((img, i) => {
        const stagger = i * 5;
        const enterSpring = spring({
          fps,
          frame: frame - stagger,
          config: { damping: 14, stiffness: 80, mass: 0.8 },
        });
        const assembleProgress = interpolate(
          frame,
          [assembleStart + stagger, assembleStart + stagger + 25],
          [0, 1],
          { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.25, 0.1, 0.25, 1) }
        );

        const s = scatteredPositions[i];
        const g = gridPositions[i] || { x: 0, y: 0 };

        // For 3-image layout, first image spans full width
        const isWideCell = count === 3 && layout.custom && i === 0;
        const thisCellW = isWideCell ? availableW : cellW;
        const thisCellH = cellH;

        const currentX = interpolate(assembleProgress, [0, 1], [(s.x / 100) * width - thisCellW / 2, g.x]);
        const currentY = interpolate(assembleProgress, [0, 1], [(s.y / 100) * height - thisCellH / 2, g.y]);
        const currentRotation = interpolate(assembleProgress, [0, 1], [s.rotation, 0]);
        const currentScale = interpolate(assembleProgress, [0, 1], [s.scale, 1]);

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: currentX,
              top: currentY,
              width: thisCellW,
              height: thisCellH,
              borderRadius: 16,
              overflow: "hidden",
              transform: `rotate(${currentRotation}deg) scale(${currentScale * Math.max(0, enterSpring)})`,
              boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
              border: "3px solid rgba(255,255,255,0.08)",
              opacity: Math.max(0, enterSpring),
            }}
          >
            <CroppedImg
              src={getImgSrc(img)}
              crop={crops?.[i]}
            />
          </div>
        );
      })}
    </AbsoluteFill>
  );
};

/**
 * CTAOutro – Shop Now / Link in Bio with animated CTA
 */
const CTAOutro = ({ ctaText, ctaSubtext, handle, accentColor, accentColor2, accentColor3, darkBg, brandName }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const ctaSpring = spring({
    fps,
    frame: frame - 5,
    config: { damping: 10, stiffness: 90, mass: 0.7 },
  });

  const handleSpring = spring({
    fps,
    frame: frame - 20,
    config: { damping: 14, stiffness: 60, mass: 1 },
  });

  // Pulsing button glow
  const pulseGlow = 0.4 + 0.3 * Math.sin((frame * Math.PI) / 15);

  // Gradient rotation for background
  const bgAngle = interpolate(frame, [0, 90], [135, 225], { extrapolateRight: "clamp" });

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(${bgAngle}deg, ${darkBg}, #1a0a2e, ${darkBg})`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {/* Decorative gradient orbs */}
      <div
        style={{
          position: "absolute",
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${accentColor}20 0%, transparent 70%)`,
          top: "10%",
          left: "-10%",
          filter: "blur(60px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${accentColor2}18 0%, transparent 70%)`,
          bottom: "10%",
          right: "-5%",
          filter: "blur(50px)",
        }}
      />

      {/* Brand name */}
      <div
        style={{
          fontSize: 30,
          fontFamily: "'Inter', system-ui, sans-serif",
          fontWeight: 300,
          letterSpacing: 10,
          color: "#ffffff66",
          textTransform: "uppercase",
          marginBottom: 40,
          opacity: interpolate(frame, [0, 15], [0, 1], { extrapolateRight: "clamp" }),
        }}
      >
        {brandName}
      </div>

      {/* CTA Button */}
      <div
        style={{
          transform: `scale(${interpolate(ctaSpring, [0, 1], [0.5, 1])})`,
          opacity: Math.max(0, ctaSpring),
        }}
      >
        <div
          style={{
            background: `linear-gradient(135deg, ${accentColor}, ${accentColor2})`,
            padding: "28px 80px",
            borderRadius: 60,
            fontSize: 42,
            fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
            fontWeight: 800,
            color: "#fff",
            letterSpacing: 4,
            textTransform: "uppercase",
            boxShadow: `0 0 ${60 * pulseGlow}px ${accentColor}88, 0 12px 40px rgba(0,0,0,0.4)`,
          }}
        >
          {ctaText}
        </div>
      </div>

      {/* Subtext */}
      <div
        style={{
          fontSize: 32,
          fontFamily: "'Inter', system-ui, sans-serif",
          fontWeight: 500,
          color: "#ffffffbb",
          marginTop: 30,
          opacity: interpolate(frame, [15, 30], [0, 1], { extrapolateRight: "clamp" }),
        }}
      >
        {ctaSubtext}
      </div>

      {/* Handle */}
      <div
        style={{
          fontSize: 28,
          fontFamily: "'Inter', system-ui, sans-serif",
          fontWeight: 600,
          background: `linear-gradient(90deg, ${accentColor}, ${accentColor2}, ${accentColor3})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
          marginTop: 50,
          transform: `translateY(${interpolate(handleSpring, [0, 1], [30, 0])}px)`,
          opacity: Math.max(0, handleSpring),
        }}
      >
        {handle}
      </div>


    </AbsoluteFill>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────

const WigGlowUp = ({
  brandName = "LUXE HAIR CO.",
  productName = "Brazilian Body Wave",
  tagline = "Your Next Signature Look",
  introTitle = "MY\nREVIEW",
  price = "$89.99",
  originalPrice = "$149.99",
  heroImage = "",
  carouselImages = [],
  galleryImages = [],
  features = [
    { icon: "💇‍♀️", label: "100% Human Hair" },
    { icon: "📏", label: '16"-28" Length' },
    { icon: "🔥", label: "Heat Friendly" },
    { icon: "💧", label: "Wash & Restyle" },
    { icon: "✨", label: "Pre-Plucked" },
    { icon: "🎀", label: "HD Lace Front" },
  ],
  colorSwatches = [
    { name: "Natural Black", color: "#1a1a1a" },
    { name: "Dark Brown", color: "#3d2314" },
    { name: "Honey Blonde", color: "#c68e3f" },
    { name: "Burgundy", color: "#722f37" },
    { name: "Ombré", color: "linear-gradient(135deg, #1a1a1a, #c68e3f)" },
  ],
  showIntro = false,
  showOutro = false,
  ctaText = "SHOP NOW",
  ctaSubtext = "Link in Bio 🔗",
  handle = "@luxehairco",
  introDuration = 75,
  heroDuration = 120,
  carouselDuration = 180,
  featureDuration = 120,
  colorDuration = 90,
  galleryDuration = 150,
  outroDuration = 90,
  transitionDuration = 20,
  accentColor = "#FF6B9D",
  accentColor2 = "#C084FC",
  accentColor3 = "#60A5FA",
  darkBg = "#0A0A0A",
  audioSrc = "",
  audioVolume = 0.8,
  audioStartFrom = 0,
  audioDurationOverride = 30,
  _crops = {},
}) => {
  const [audioError, setAudioError] = useState(false);
  const audioSource = useMemo(() => {
    if (!audioSrc) return null;
    try {
      return audioSrc.startsWith("http") ? audioSrc : staticFile(audioSrc);
    } catch {
      return null;
    }
  }, [audioSrc]);

  // Determine if we have enough data for optional sections
  const hasCarousel = carouselImages.length >= 2;
  const hasGallery = galleryImages.length >= 2;
  const hasFeatures = features.length > 0;
  const hasColors = colorSwatches.length > 0;

  return (
    <AbsoluteFill style={{ background: darkBg }}>
      {/* Global audio */}
      {audioSource && !audioError && (
        <Audio
          src={audioSource}
          volume={audioVolume}
          startFrom={audioStartFrom * 30}
          onError={(error) => {
            console.warn("WigGlowUp audio failed to load", error);
            setAudioError(true);
          }}
        />
      )}

      <TransitionSeries>
        {/* 1. Intro Splash (optional) */}
        {showIntro && (
          <>
            <TransitionSeries.Sequence durationInFrames={introDuration}>
              <IntroSplash
                brandName={brandName}
                tagline={tagline}
                introTitle={introTitle}
                accentColor={accentColor}
                accentColor2={accentColor2}
                accentColor3={accentColor3}
                darkBg={darkBg}
              />
            </TransitionSeries.Sequence>

            <TransitionSeries.Transition
              presentation={fade()}
              timing={linearTiming({ durationInFrames: transitionDuration })}
            />
          </>
        )}

        {/* 2. Hero Reveal */}
        <TransitionSeries.Sequence durationInFrames={heroDuration}>
          <HeroReveal
            heroImage={heroImage}
            heroCrop={_crops.heroImage || null}
            productName={productName}
            tagline={tagline}
            accentColor={accentColor}
            accentColor2={accentColor2}
            darkBg={darkBg}
          />
        </TransitionSeries.Sequence>

        {/* 3. Image Carousel (if enough images) */}
        {hasCarousel && (
          <>
            <TransitionSeries.Transition
              presentation={slide({ direction: "from-left" })}
              timing={linearTiming({ durationInFrames: transitionDuration })}
            />
            <TransitionSeries.Sequence durationInFrames={carouselDuration}>
              <ImageCarousel
                images={carouselImages}
                crops={_crops.carouselImages || null}
                accentColor={accentColor}
                accentColor2={accentColor2}
                darkBg={darkBg}
              />
            </TransitionSeries.Sequence>
          </>
        )}

        {/* 4. Feature Highlights (if features provided) */}
        {hasFeatures && (
          <>
            <TransitionSeries.Transition
              presentation={fade()}
              timing={linearTiming({ durationInFrames: transitionDuration })}
            />
            <TransitionSeries.Sequence durationInFrames={featureDuration}>
              <FeatureHighlights
                features={features}
                accentColor={accentColor}
                accentColor2={accentColor2}
                darkBg={darkBg}
                productName={productName}
              />
            </TransitionSeries.Sequence>
          </>
        )}

        {/* 5. Color Swatches (if swatches provided) */}
        {hasColors && (
          <>
            <TransitionSeries.Transition
              presentation={fade()}
              timing={linearTiming({ durationInFrames: transitionDuration })}
            />
            <TransitionSeries.Sequence durationInFrames={colorDuration}>
              <ColorSwatchSection
                colorSwatches={colorSwatches}
                accentColor={accentColor}
                accentColor2={accentColor2}
                darkBg={darkBg}
                productName={productName}
              />
            </TransitionSeries.Sequence>
          </>
        )}

        {/* 6. Gallery Burst (if enough images) */}
        {hasGallery && (
          <>
            <TransitionSeries.Transition
              presentation={slide({ direction: "from-right" })}
              timing={linearTiming({ durationInFrames: transitionDuration })}
            />
            <TransitionSeries.Sequence durationInFrames={galleryDuration}>
              <GalleryBurst
                images={galleryImages}
                crops={_crops.galleryImages || null}
                accentColor={accentColor}
                darkBg={darkBg}
              />
            </TransitionSeries.Sequence>
          </>
        )}

        {/* 7. CTA Outro (optional) */}
        {showOutro && (
          <>
            <TransitionSeries.Transition
              presentation={fade()}
              timing={linearTiming({ durationInFrames: transitionDuration })}
            />

            <TransitionSeries.Sequence durationInFrames={outroDuration}>
              <CTAOutro
                ctaText={ctaText}
                ctaSubtext={ctaSubtext}
                handle={handle}
                accentColor={accentColor}
                accentColor2={accentColor2}
                accentColor3={accentColor3}
                darkBg={darkBg}
                brandName={brandName}
              />
            </TransitionSeries.Sequence>
          </>
        )}
      </TransitionSeries>
    </AbsoluteFill>
  );
};

// ─── Calculate Metadata ─────────────────────────────────────────────────────

export const calculateWigGlowUpMetadata = async ({ props }) => {
  const {
    showIntro = false,
    showOutro = false,
    introDuration = 75,
    heroDuration = 120,
    carouselDuration = 180,
    featureDuration = 120,
    colorDuration = 90,
    galleryDuration = 150,
    outroDuration = 90,
    transitionDuration = 20,
    carouselImages = [],
    galleryImages = [],
    features = [],
    colorSwatches = [],
    audioSrc,
    audioDurationOverride,
    audioStartFrom = 0,
  } = props;

  const hasCarousel = (carouselImages || []).length >= 2;
  const hasGallery = (galleryImages || []).length >= 2;
  const hasFeatures = (features || []).length > 0;
  const hasColors = (colorSwatches || []).length > 0;

  // Calculate total frames from section durations (hero is always present)
  let totalFrames = heroDuration;
  let transitionCount = 0;

  if (showIntro) {
    totalFrames += introDuration;
    transitionCount += 1; // intro→hero
  }
  if (hasCarousel) {
    totalFrames += carouselDuration;
    transitionCount += 1; // hero→carousel transition
  }
  if (hasFeatures) {
    totalFrames += featureDuration;
    transitionCount += 1;
  }
  if (hasColors) {
    totalFrames += colorDuration;
    transitionCount += 1;
  }
  if (hasGallery) {
    totalFrames += galleryDuration;
    transitionCount += 1;
  }
  if (showOutro) {
    totalFrames += outroDuration;
    transitionCount += 1;
  }

  // Subtract overlapping transition frames
  totalFrames -= transitionCount * transitionDuration;

  // If audio is provided, use audio duration; otherwise fall back to sequence-based duration
  if (audioSrc && audioDurationOverride) {
    const audioFrames = Math.ceil((audioDurationOverride - audioStartFrom) * 30);
    return {
      durationInFrames: Math.max(90, audioFrames),
    };
  }

  return {
    durationInFrames: Math.max(90, totalFrames),
  };
};

export default WigGlowUp;

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
